import { getLocation } from "connected-react-router";
import { call, put, select, takeEvery } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import User from "@mapmycustomers/shared/types/User";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import Path from "@app/enum/Path";
import { CALENDAR_VIEW_STATE } from "@app/scene/activity/component/ActivityCalendarPage/store/saga";
import { SCHEDULER_VIEW_STATE } from "@app/scene/activity/component/ActivitySchedulerPage/store/saga";
import ViewMode from "@app/scene/activity/enum/ViewMode";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { getSimpleConditionForField } from "@app/util/filters/utils";

import localSettings from "../../config/LocalSettings";
import Iam from "../../types/Iam";
import { getMe } from "../iam";

import { initializeUserColors, setSelectedUserIds } from "./actions";
import { getUserColorSelectedUserIds } from "./selectors";

export function* onSetUserColors(viewState: Partial<ViewState>) {
  const assigneeCondition = viewState.filter
    ? getSimpleConditionForField(viewState.filter, ActivityFieldName.ASSIGNEE, EntityType.ACTIVITY)
    : undefined;
  if (assigneeCondition) {
    const me: Iam = yield select(getMe);
    let restUserIds: User["id"][] = [];
    const updated: User["id"][] = assigneeCondition.value.filter((id: User["id"]) => id !== me.id);
    const updatedSet = new Set(updated);
    let existing: User["id"][] = yield select(getUserColorSelectedUserIds);
    existing = existing.slice(1);

    const newUserIds = updated.filter((id) => !existing.includes(id));
    let numberOfIdsToInsertAtSamePosition = updated.length - newUserIds.length;

    restUserIds = existing.reduce<User["id"][]>((result, userId) => {
      let idToInsert;
      if (updatedSet.has(userId)) {
        numberOfIdsToInsertAtSamePosition--;
        idToInsert = userId;
      } else if (newUserIds.length) {
        idToInsert = newUserIds.shift();
      } else if (numberOfIdsToInsertAtSamePosition > 0) {
        idToInsert = -1;
      }
      if (isDefined(idToInsert)) {
        result.push(idToInsert);
      }
      return result;
    }, []);
    restUserIds = [...restUserIds, ...newUserIds];
    // we don't update user colors if provided view state doesn't have assignee filter
    // this needs to avoid dropping colors after any view state changing.
    yield put(
      setSelectedUserIds([
        // Put current user id to first place, independently does current user in filter of view state OR not
        // current user should have color with index 0
        me.id,
        ...restUserIds,
      ])
    );
  }
}

export function* onInitializeUserColors() {
  const { pathname } = yield select(getLocation);

  // By default, we initialize user colors for the Calendar View, except
  // when the app is loaded on the Scheduler View.
  const viewState: undefined | ViewState = localSettings.getViewSettings(
    pathname.startsWith(`${Path.ACTIVITY}/${ViewMode.SCHEDULER}`)
      ? SCHEDULER_VIEW_STATE
      : CALENDAR_VIEW_STATE,
    activityFieldModel
  );

  const me: Iam = yield select(getMe);
  // initially we put current user id in first place of user colors
  // current user should have color with index 0
  yield put(setSelectedUserIds([me.id]));

  // if viewState is available we call onSetUserColors action to set colors for all selected users
  if (viewState) {
    yield call(onSetUserColors, viewState);
  }
}

export function* userColorSaga() {
  yield takeEvery(initializeUserColors, onInitializeUserColors);
}
